import axios from 'axios';
import {getApiConfig} from '../config/configProvider';
import {INVOICE_UPLOAD_ANONYMOUS_CALL_GAP_MS} from '../constants/constants';

const getBaseUrl = () => {
  const config = getApiConfig();
  return config && config.invoiceScanningUrl
    ? config.invoiceScanningUrl
    : '/api/invoice-scanning/api/customer/invoices';
};

// Anonymous (not-logged-in) uploads go through a separate, unauthenticated
// endpoint — see anonymous-price-comparison-api.pdf. Same-origin via the ecom
// gateway, no CORS/proxy hop, same as the authenticated invoiceScanningUrl.
const getPublicBaseUrl = () => {
  const config = getApiConfig();
  return config && config.publicInvoiceScanningUrl
    ? config.publicInvoiceScanningUrl
    : '/api/invoice-scanning/api/public/invoices';
};

// Referral scans are uploaded by account-signup-svc on the referrer's behalf, so
// they belong to the uploading employee rather than any account the customer
// endpoint above would authorise by — they're read through the referral
// endpoint, which authorises the uploader (or staff).
const getReferralBaseUrl = () => {
  const config = getApiConfig();
  return config && config.referralInvoiceScanningUrl
    ? config.referralInvoiceScanningUrl
    : '/api/invoice-scanning/api/invoices';
};

// A cancelled request (e.g. the app's idle-mode handling) resolves to `null`
// via the global axios response interceptor rather than rejecting — guard
// against that here and throw a tagged, catchable error instead of a raw
// TypeError reading `.data`. Tagging it (rather than just returning
// null/undefined) lets callers tell "request was interrupted" apart from a
// genuine auth failure — treating the two the same previously caused a
// backgrounded poll tick to force a full re-login and wipe the page state.
const extractData = res => {
  if (!res) {
    const err = new Error('Request was interrupted. Please try again.');
    err.requestInterrupted = true;
    throw err;
  }
  return res.data;
};

// Each call reserves the next slot synchronously (before awaiting anything),
// so calls fired in the same tick queue up correctly instead of all reading
// the same "last call" time and deciding none of them need to wait.
let nextAllowedAnonymousCallAt = 0;

const throttleAnonymousCall = () => {
  const now = Date.now();
  const waitMs = Math.max(0, nextAllowedAnonymousCallAt - now);
  nextAllowedAnonymousCallAt =
    Math.max(now, nextAllowedAnonymousCallAt) +
    INVOICE_UPLOAD_ANONYMOUS_CALL_GAP_MS;
  return waitMs > 0
    ? new Promise(resolve => setTimeout(resolve, waitMs))
    : Promise.resolve();
};

export default class InvoiceUploadApi {
  static upload(files, fulfillmentType, headers) {
    const url = getBaseUrl();
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    if (fulfillmentType) {
      formData.append('fulfillmentType', fulfillmentType);
    }
    return axios
      .post(`${url}/upload`, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(extractData);
  }

  static list(page, pageSize, headers) {
    const url = getBaseUrl();
    return axios
      .get(url, {headers, params: {page, pageSize}})
      .then(extractData);
  }

  static getDetail(id, headers) {
    const url = getBaseUrl();
    return axios.get(`${url}/${id}`, {headers}).then(extractData);
  }

  // Same CustomerInvoiceDetail payload as getDetail, for the scan of a
  // referral signup's document (processId from the signup's
  // invoiceScanningUploads).
  static getReferralComparison(id, headers) {
    const url = getReferralBaseUrl();
    return axios
      .get(`${url}/${id}/referral-comparison`, {headers})
      .then(extractData);
  }

  // Anonymous upload — deliberately never sends an Authorization header; a
  // request carrying a valid token is rejected with 409 (that caller belongs
  // on the authenticated upload() above instead).
  static async uploadAnonymous(files, fulfillmentType) {
    await throttleAnonymousCall();
    const url = getPublicBaseUrl();
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    if (fulfillmentType) {
      formData.append('fulfillmentType', fulfillmentType);
    }
    return axios
      .post(`${url}/upload`, formData, {
        headers: {'Content-Type': 'multipart/form-data'},
      })
      .then(extractData);
  }

  // No auth header, and none is rejected here — a visitor who registers or
  // logs in mid-comparison must not lose the results they're looking at.
  static async getDetailAnonymous(id) {
    await throttleAnonymousCall();
    const url = getPublicBaseUrl();
    return axios.get(`${url}/${id}`).then(extractData);
  }

  static async getAnonymousQuota() {
    await throttleAnonymousCall();
    const url = getPublicBaseUrl();
    return axios.get(`${url}/quota`).then(extractData);
  }
}
